/**
 * restlicht.fyi :: DB-Typen fuer die Schema-Erweiterung
 * IDs der `substances`-Tabelle sind uuid (string). Detail-Tabellen-PKs
 * bleiben `bigint` (number).
 */

// ---------- Enums ----------
export type RoaType =
  | "oral"
  | "sublingual"
  | "buccal"
  | "insufflated"
  | "smoked"
  | "vaporized"
  | "rectal"
  | "intravenous"
  | "intramuscular"
  | "subcutaneous"
  | "transdermal"
  | "other";

export type DoseTier = "threshold" | "light" | "common" | "strong" | "heavy";

export type EffectKind = "desired" | "neutral" | "adverse";

export type InteractionRisk =
  | "low_risk_synergy"
  | "low_risk_no_synergy"
  | "low_risk_decrease"
  | "caution"
  | "unsafe"
  | "dangerous"
  | "unknown";

export type ConfidenceLevel = "low" | "medium" | "high";

// ---------- Hilfstypen ----------
export interface SourceRef {
  title: string;
  url: string;
}

// ---------- Tabellen-Rows ----------
export interface SubstanceProfile {
  substance_id: string;
  summary: string | null;
  summary_en: string | null;
  dose_note: string | null;
  dose_note_en: string | null;
  general_notes: string | null;
  general_notes_en: string | null;
  tolerance_notes: string | null;
  tolerance_notes_en: string | null;
  risk_notes: string | null;
  risk_notes_en: string | null;
  harm_reduction: string | null;
  harm_reduction_en: string | null;
  confidence: ConfidenceLevel;
  sources: SourceRef[];
  published: boolean;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubstanceRoute {
  id: number;
  substance_id: string;
  route: RoaType;
  bioavailability_pct: number | null;
  onset_min_minutes: number | null;
  onset_max_minutes: number | null;
  duration_min_minutes: number | null;
  duration_max_minutes: number | null;
  after_effects_min_minutes: number | null;
  after_effects_max_minutes: number | null;
  notes: string | null;
  notes_en: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface SubstanceDose {
  id: number;
  route_id: number;
  tier: DoseTier;
  amount_min: number | null;
  amount_max: number | null;
  unit: string;
  notes: string | null;
  notes_en: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubstanceEffect {
  id: number;
  substance_id: string;
  kind: EffectKind;
  text: string;
  text_en: string | null;
  sort_order: number;
  created_at: string;
}

export type Evidenz = "gesichert" | "wahrscheinlich" | "unzureichend";

export interface InteractionContent {
  kernaussage: string;
  was_passiert: string;
  fuer_wen: string;
  warnzeichen: string[];
  warnzeichen_hinweis: string;
  wenn_passiert: string[];
  experte: string[];
  evidenz: Evidenz;
  quellen: string[];
}

export interface InteractionDetail {
  id: number;
  substance_low_id: string;
  substance_high_id: string;
  risk_level: InteractionRisk;
  markers: string[] | null;
  content_de: InteractionContent | null;
  content_en: InteractionContent | null;
  summary: string | null;
  mechanism: string | null;
  effects_combined: string | null;
  risks: string | null;
  harm_reduction: string | null;
  confidence: ConfidenceLevel;
  sources: SourceRef[];
  published: boolean;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface RouteWithDoses extends SubstanceRoute {
  substance_doses: SubstanceDose[];
}

export interface SubstanceDetail {
  id: string;
  name: string;
  substance_profiles: SubstanceProfile | null;
  substance_routes: RouteWithDoses[];
  substance_effects: SubstanceEffect[];
}

export interface ComboCardData {
  a: SubstanceDetail;
  b: SubstanceDetail;
  interaction: InteractionDetail | null;
}

// ---------- UI-Helfer ----------
/**
 * `tone` must stay a literal hex value: the UI appends alpha suffixes
 * (`${tone}55`), which is invalid for CSS custom properties.
 */
export const RISK_META: Record<
  InteractionRisk,
  { label: string; accent: "cyan" | "violet" | "amber"; tone: string }
> = {
  low_risk_synergy: { label: "Niedriges Zusatzrisiko + Synergie", accent: "cyan", tone: "#4A90D9" },
  low_risk_no_synergy: { label: "Niedriges Zusatzrisiko", accent: "cyan", tone: "#3FB68B" },
  low_risk_decrease: { label: "Niedriges Zusatzrisiko + Abschwächung", accent: "cyan", tone: "#8A94A6" },
  caution: { label: "Vorsicht", accent: "amber", tone: "#E0B73A" },
  unsafe: { label: "Unsicher", accent: "amber", tone: "#DB8B3A" },
  dangerous: { label: "Gefährlich", accent: "violet", tone: "#C0455B" },
  unknown: { label: "Unbekannt / unzureichend untersucht", accent: "violet", tone: "#6B7280" },
};

/** Safe lookup: never returns undefined, unmapped values become "unknown". */
export function riskMeta(value: unknown) {
  if (typeof value === "string" && Object.prototype.hasOwnProperty.call(RISK_META, value)) {
    return RISK_META[value as InteractionRisk];
  }
  return RISK_META.unknown;
}

export const DOSE_TIER_LABEL: Record<DoseTier, string> = {
  threshold: "Schwelle",
  light: "Leicht",
  common: "Normal",
  strong: "Stark",
  heavy: "Hoch",
};

// ---------- Marker-Metadaten (Legende/Tooltips) ----------
export interface MarkerMeta {
  label: string;
  description: string;
  tone: string;
  emoji: string;
}

export const MARKER_META: Record<string, MarkerMeta> = {
  serotonin: {
    label: "Serotonerg",
    description: "Wirkt stark auf den Serotoninhaushalt. Kombinationen koennen ein Serotonin-Syndrom ausloesen.",
    tone: "#be185d",
    emoji: "Serotonin",
  },
  cardio: {
    label: "Kardiovaskulaer",
    description: "Belastet Herz und Kreislauf. Erhoehte Risiken fuer Blutdruck, Herzrhythmus und Gefaesssystem.",
    tone: "#ea580c",
    emoji: "Herz",
  },
  serotonin_cardio: {
    label: "Serotonerg + Kardio",
    description: "Doppelbelastung: serotonerge und kardiovaskulaere Effekte verstaerken sich gegenseitig.",
    tone: "#dc2626",
    emoji: "Serotonin+Herz",
  },
  respiratory: {
    label: "Atemdepressiv",
    description: "Daempft die Atmung. Kombinationen mit anderen Atemdepressiva sind potenziell toedlich.",
    tone: "#1f2937",
    emoji: "Atem",
  },
};

